import { useState, useEffect, useCallback } from 'react'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../../components/ui/card'
import { Switch } from '../../../components/ui/switch'
import { Label } from '../../../components/ui/label'
import { Button } from '../../../components/ui/button'
import { Loader2, SlidersHorizontal } from 'lucide-react'

export interface ModelRow {
  id: string
  label: string
  enabled: boolean
}

interface ChatModelVisibilityEditorProps {
  title: string
  description: string
  getUrl: string
  putUrl: string
  /** When set, a "Use global defaults" control is shown when overrides exist */
  deleteUrl?: string
  className?: string
}

export function ChatModelVisibilityEditor({
  title,
  description,
  getUrl,
  putUrl,
  deleteUrl,
  className = '',
}: ChatModelVisibilityEditorProps) {
  const [rows, setRows] = useState<ModelRow[]>([])
  const [loading, setLoading] = useState(true)
  const [savingId, setSavingId] = useState<string | null>(null)
  const [clearing, setClearing] = useState(false)
  const [hasCustomOverrides, setHasCustomOverrides] = useState(false)
  const [message, setMessage] = useState<{ type: 'ok' | 'err'; text: string } | null>(null)

  const load = useCallback(async () => {
    setLoading(true)
    setMessage(null)
    try {
      const res = await fetch(getUrl, { credentials: 'include' })
      if (!res.ok) {
        setMessage({ type: 'err', text: 'Could not load model configuration.' })
        setRows([])
        setHasCustomOverrides(false)
        return
      }
      const data = (await res.json()) as {
        models?: ModelRow[]
        hasCustomOverrides?: boolean
      }
      setRows(Array.isArray(data.models) ? data.models : [])
      setHasCustomOverrides(Boolean(data.hasCustomOverrides))
    } catch {
      setMessage({ type: 'err', text: 'Network error while loading.' })
      setRows([])
      setHasCustomOverrides(false)
    } finally {
      setLoading(false)
    }
  }, [getUrl])

  useEffect(() => {
    void load()
  }, [load])

  const enabledCount = rows.filter((r) => r.enabled).length

  const handleToggle = async (id: string, enabled: boolean) => {
    const row = rows.find((r) => r.id === id)
    if (!row) return
    if (row.enabled && !enabled && enabledCount <= 1) {
      setMessage({
        type: 'err',
        text: 'At least one model must remain enabled for the chat picker.',
      })
      return
    }

    const next = rows.map((r) => (r.id === id ? { ...r, enabled } : r))
    setRows(next)
    setSavingId(id)
    setMessage(null)
    try {
      const res = await fetch(putUrl, {
        method: 'PUT',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          models: next.map(({ id: mid, enabled: en }) => ({ id: mid, enabled: en })),
        }),
      })
      const body = await res.json().catch(() => ({}))
      if (!res.ok) {
        const detail =
          typeof body.detail === 'string'
            ? body.detail
            : Array.isArray(body.detail)
              ? body.detail.map((x: { msg?: string }) => x.msg).filter(Boolean).join(' ')
              : 'Save failed'
        setMessage({ type: 'err', text: detail })
        await load()
        return
      }
      if (Array.isArray(body.models)) {
        setRows(body.models)
      }
      setHasCustomOverrides(Boolean(body.hasCustomOverrides))
      setMessage({ type: 'ok', text: 'Saved.' })
    } catch {
      setMessage({ type: 'err', text: 'Network error while saving.' })
      await load()
    } finally {
      setSavingId(null)
    }
  }

  const handleClearOverrides = async () => {
    if (!deleteUrl) return
    setClearing(true)
    setMessage(null)
    try {
      const res = await fetch(deleteUrl, { method: 'DELETE', credentials: 'include' })
      const body = await res.json().catch(() => ({}))
      if (!res.ok) {
        setMessage({ type: 'err', text: typeof body.detail === 'string' ? body.detail : 'Could not revert.' })
        return
      }
      if (Array.isArray(body.models)) {
        setRows(body.models)
      }
      setHasCustomOverrides(Boolean(body.hasCustomOverrides))
      setMessage({ type: 'ok', text: 'This user now follows global model visibility.' })
    } catch {
      setMessage({ type: 'err', text: 'Network error.' })
    } finally {
      setClearing(false)
    }
  }

  return (
    <div className={`space-y-6 max-w-2xl ${className}`}>
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-lg">
            <SlidersHorizontal className="h-5 w-5 text-indigo-500" />
            {title}
          </CardTitle>
          <CardDescription>{description}</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {deleteUrl && hasCustomOverrides && (
            <div className="flex flex-wrap items-center gap-3">
              <Button
                type="button"
                variant="outline"
                size="sm"
                disabled={clearing || loading || !!savingId}
                onClick={() => void handleClearOverrides()}
              >
                {clearing ? (
                  <span className="flex items-center gap-2">
                    <Loader2 className="h-4 w-4 animate-spin" />
                    Reverting…
                  </span>
                ) : (
                  'Use global defaults'
                )}
              </Button>
              <span className="text-xs text-muted-foreground">
                Removes per-user overrides; the model pill uses the same list as Model Configuration.
              </span>
            </div>
          )}
          {message && (
            <p
              className={`text-sm ${message.type === 'err' ? 'text-red-600 dark:text-red-400' : 'text-emerald-600 dark:text-emerald-400'}`}
            >
              {message.text}
            </p>
          )}
          {loading ? (
            <div className="flex items-center gap-2 text-sm text-muted-foreground py-6">
              <Loader2 className="h-4 w-4 animate-spin" />
              Loading…
            </div>
          ) : rows.length === 0 ? (
            <p className="text-sm text-muted-foreground py-4">
              No models are defined in the LLM configuration.
            </p>
          ) : (
            <ul className="divide-y rounded-lg border border-border">
              {rows.map((row) => (
                <li
                  key={row.id}
                  className="adm-model-row flex items-center justify-between gap-4 px-4 py-3"
                >
                  <div className="min-w-0">
                    <div className="font-medium text-foreground truncate">{row.label}</div>
                    <div className="text-xs text-muted-foreground font-mono truncate">{row.id}</div>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    {savingId === row.id && (
                      <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
                    )}
                    <div className="flex items-center gap-2">
                      <Label
                        htmlFor={`model-${row.id}`}
                        className="text-sm text-muted-foreground cursor-pointer"
                      >
                        Listed in chat
                      </Label>
                      <Switch
                        id={`model-${row.id}`}
                        checked={row.enabled}
                        disabled={!!savingId || clearing}
                        onCheckedChange={(checked) => void handleToggle(row.id, checked)}
                      />
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </CardContent>
      </Card>
    </div>
  )
}
